import { ChangeDetectorRef, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { environment } from '@/environments/environment';
import { Router, NavigationEnd, ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LayoutService } from '../../service/layout.service';
import { UserStateService, AuthService, UsuariosService, ImpersonationService, TopbarSearchService } from '@/app/core';
import { Usuario } from '@/app/models';
import { Subject, takeUntil, filter, fromEvent } from 'rxjs';
import { DialogComponent } from '@/app/shared/dialog/dialog.component';
import { PreMaintenanceBannerComponent } from '@/app/shared';
import { resolveCompetencia } from '@/app/shared/types/user-profile.types';
import { ImpersonationAuditState } from '@/app/core/service/impersonation.service';
import { PageTitleService } from '../../service/page-title.service';
import { AiChatPanelComponent } from './ai-chat-panel/ai-chat-panel.component';
import { AppMenuService } from '../../service/menu.service';
import { MenuItem } from 'primeng/api';
import { NotificationsStore } from '@/app/core/notifications/notifications.store';
import {
    NotificationSeverity,
    kindLabel,
    notificationSeverity,
    resolveNotificationRoute,
} from '@/app/core/notifications/notifications-navigation.util';
import { UserNotification } from '@/app/core/notifications/notifications.types';

interface NavSearchEntry {
    label: string;
    icon: string | undefined;
    section: string;
    routerLink: string[];
}

function flattenMenuItems(items: MenuItem[], rootSection = ''): NavSearchEntry[] {
    const entries: NavSearchEntry[] = [];
    for (const item of items) {
        const isGroup = !item.routerLink?.length && !!item.items?.length;
        const section = rootSection || (isGroup ? (item.label ?? '') : '');

        if (item.routerLink?.length) {
            entries.push({
                label: item.label ?? '',
                icon: item.icon,
                section: rootSection,
                routerLink: item.routerLink as string[],
            });
        }
        if (item.items?.length) {
            entries.push(...flattenMenuItems(item.items, isGroup ? (item.label ?? rootSection) : rootSection));
        }
    }
    return entries;
}

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, DialogComponent, PreMaintenanceBannerComponent ],
    templateUrl: './app-topbar.html',
    styleUrl: './app-topbar.scss'
})
export class AppTopbar implements OnDestroy {
    readonly layoutService = inject(LayoutService);
    readonly titleService = inject(PageTitleService);
    private readonly userState = inject(UserStateService);
    private readonly usuariosService = inject(UsuariosService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly auth = inject(AuthService);
    private readonly impersonation = inject(ImpersonationService);
    private readonly searchService = inject(TopbarSearchService);
    private readonly menuService = inject(AppMenuService);
    private readonly cdr = inject(ChangeDetectorRef);
    readonly notificationsStore = inject(NotificationsStore);

    user: Usuario | undefined;
    profileOpen = false;
    aiChatOpen = false;
    notifOpen = false;
    searchQuery = '';
    searchResults: NavSearchEntry[] = [];
    searchDropdownOpen = false;
    showEnvBadge = environment.showEnvBadge;
    envName = environment.envName;
    auditState: ImpersonationAuditState | null = null;
    private readonly defaultTitle = typeof document !== 'undefined' ? document.title : 'SIGD';

    get isLogoClickable(): boolean {
        const url = this.router.url.split('?')[0];
        return !url.startsWith('/sections') && !url.startsWith('/calidad');
    }

    get avatarLetters(): string {
        const n = this.user?.nombre?.trim() || '';
        const a = this.user?.apellido?.trim() || '';
        return ((n[0] || '') + (a[0] || '')).toUpperCase() || 'U';
    }

    get avatarUrl(): string | null {
        if (!this.user?.avatar) return null;
        if (this.user.avatar.startsWith('http')) return this.user.avatar;
        const base = environment.api.endsWith('/') ? environment.api.slice(0, -1) : environment.api;
        const avatarPath = this.user.avatar.startsWith('/') ? this.user.avatar : `/${this.user.avatar}`;
        return `${base}${avatarPath}`;
    }

    private destroy$ = new Subject<void>();
    @ViewChild(DialogComponent) dialog?: DialogComponent;

    async ngOnInit() {
        this.syncImpersonationUi();

        fromEvent<MouseEvent>(document, 'click')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                let changed = false;
                if (this.profileOpen)        { this.profileOpen        = false; changed = true; }
                if (this.aiChatOpen)         { this.aiChatOpen         = false; changed = true; }
                if (this.notifOpen)          { this.notifOpen          = false; changed = true; }
                if (this.searchDropdownOpen) { this.searchDropdownOpen = false; changed = true; }
                if (changed) this.cdr.detectChanges();
            });

        fromEvent(window, 'impersonation-state-changed')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.syncImpersonationUi());

        fromEvent<StorageEvent>(window, 'storage')
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (event.key === 'impersonation_state_v1') this.syncImpersonationUi();
            });

        this.usuariosService
            .getCurrentUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (u) => { this.user = u; this.cdr.detectChanges(); },
                error: () => { const cached = this.userState.currentUser; if (cached) this.user = cached as any; }
            });

        // Resolver título desde datos de la ruta activa
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.clearSearch();
            this.resolveRouteTitleData();
        });

        // Resolver título inicial al cargar
        this.resolveRouteTitleData();
    }

    private resolveRouteTitleData(): void {
        let route = this.activatedRoute.snapshot;
        while (route.firstChild) route = route.firstChild;
        const data = route.data;
        const title = data['title'] || route.title;
        if (title) {
            this.titleService.set(title, data['breadcrumb']);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.updateDocumentTitle(this.defaultTitle);
    }

    onSearchChange(query: string): void {
        this.searchService.set(query);
        const q = query.trim().toLowerCase();
        if (!q) {
            this.searchResults = [];
            this.searchDropdownOpen = false;
            return;
        }
        const allItems = flattenMenuItems(this.menuService.getMenuItems());
        this.searchResults = allItems
            .filter(e =>
                e.label.toLowerCase().includes(q) ||
                e.section.toLowerCase().includes(q)
            )
            .slice(0, 10);
        this.searchDropdownOpen = true;
    }

    onSearchFocus(): void {
        if (this.searchQuery.trim() && this.searchResults.length) {
            this.searchDropdownOpen = true;
        }
    }

    navigateToSearchResult(entry: NavSearchEntry): void {
        this.router.navigate(entry.routerLink);
        this.clearSearch();
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.searchResults = [];
        this.searchDropdownOpen = false;
        this.searchService.clear();
    }

    get unreadNotifCount(): number {
        return this.notificationsStore.unread();
    }

    get previewNotifications(): UserNotification[] {
        return this.notificationsStore.unreadItems().slice(0, 8);
    }

    notificationSeverity(notification: UserNotification): NotificationSeverity {
        return notificationSeverity(notification);
    }

    notificationKindLabel(kind: string): string {
        return kindLabel(kind);
    }

    relativeNotificationTime(value: string): string {
        const createdAt = new Date(value).getTime();
        const minutes = Math.floor((Date.now() - createdAt) / 60_000);
        if (minutes < 1) return 'Ahora';
        if (minutes < 60) return `Hace ${minutes} min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `Hace ${hours} h`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `Hace ${days} d`;
        return new Date(value).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
    }

    toggleNotif() {
        this.profileOpen = false;
        this.aiChatOpen  = false;
        this.notifOpen   = !this.notifOpen;
        if (this.notifOpen) {
            void this.notificationsStore.refresh({ limit: 25, silent: true });
        }
    }

    async openNotification(notification: UserNotification): Promise<void> {
        if (!notification.read) {
            await this.notificationsStore.markRead(notification.id);
        }
        this.notifOpen = false;

        const target = resolveNotificationRoute(notification);
        if (target) {
            await this.router.navigateByUrl(target);
            return;
        }
        await this.router.navigateByUrl('/notificaciones');
    }

    async markAllNotificationsRead(event: Event): Promise<void> {
        event.stopPropagation();
        try {
            await this.notificationsStore.markAllRead();
        } catch (error: unknown) {
            const message = error instanceof Error
                ? error.message
                : 'No se pudieron marcar las notificaciones.';
            console.warn('[AppTopbar]', message);
        }
    }

    goNotificaciones() {
        this.notifOpen = false;
        this.router.navigate(['/notificaciones']);
    }

    toggleProfile() {
        this.aiChatOpen = false;
        this.notifOpen  = false;
        this.profileOpen = !this.profileOpen;
    }

    toggleAiChat() {
        this.profileOpen = false;
        this.notifOpen   = false;
        this.aiChatOpen  = !this.aiChatOpen;
    }

    goPerfil() {
        this.router.navigate(['/perfil']);
        this.profileOpen = false;
    }

    backToSection() {
        this.profileOpen = false;
        this.router.navigate(['/sections']);
    }

    logout() {
        this.profileOpen = false;
        if (this.dialog) {
            this.dialog.confirm('¿Está seguro de cerrar sesión?', 'Cerrar Sesión', () => this.auth.logout());
        } else {
            if (confirm('¿Está seguro de cerrar sesión?')) this.auth.logout();
        }
    }

    get isImpersonating(): boolean { return !!this.auditState; }
    get auditedByName(): string { return this.auditState?.auditedByName || 'Usuario'; }
    get targetName(): string { return this.auditState?.targetName || 'Usuario'; }

    stopImpersonation() {
        const exitRoute = this.impersonation.resolveAdminExitRoute(this.auditState?.sistemaId);
        const isDetached = this.impersonation.isDetachedSession();
        this.profileOpen = false;
        this.impersonation.stop();
        if (isDetached) {
            try { window.close(); } catch {}
            setTimeout(() => {
                if (this.impersonation.isImpersonating()) return;
                this.router.navigate([exitRoute]);
            }, 150);
            return;
        }
        this.router.navigate([exitRoute]);
    }

    private syncImpersonationUi() {
        this.auditState = this.impersonation.getAuditState();
        if (!this.auditState) { this.updateDocumentTitle(this.defaultTitle); return; }
        this.updateDocumentTitle(`[AUDITADA] ${this.targetName} · ${this.defaultTitle}`);
    }

    private updateDocumentTitle(title: string) {
        if (typeof document === 'undefined') return;
        if (document.title === title) return;
        document.title = title;
    }
}
